import { useEffect, useState } from "react";
import { X, Loader2, Footprints, UserCheck, Check, ChevronDown, ChevronRight, Flag } from "lucide-react";
import { api } from "../../lib/api";
import TierBadge from "./TierBadge";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch (_) { return iso; }
};

const KIND_LABEL = {
  founder_business: "Founder · Business",
  founder_project: "Founder · Project",
  funder: "Funder",
  admin: "Admin",
};

/** Role-aware milestone journey + raw page-view history for one user. */
export default function UserJourneyModal({ user, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [showViews, setShowViews] = useState(false);

  useEffect(() => {
    if (!user) return;
    setData(null); setError(""); setShowViews(false);
    api.get(`/admin/users/${user.user_id}/journey`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail || "Couldn't load journey"));
  }, [user]);

  if (!user) return null;
  const milestones = data?.milestones || [];

  return (
    <div data-testid="user-journey-modal" role="dialog" aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="flex max-h-[85vh] w-full max-w-xl flex-col rounded-2xl border border-neutral-800 bg-neutral-950 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#F59E0B]">
              User journey{data?.journey_kind ? ` · ${KIND_LABEL[data.journey_kind] || data.journey_kind}` : ""}
            </p>
            <h3 className="mt-1 text-lg font-extrabold text-neutral-100">{user.name || user.email}</h3>
            {data && (
              <p className="mt-1 text-xs text-neutral-400" data-testid="journey-summary">
                {data.total_views} page views
                {data.first_seen && <> · first seen {fmt(data.first_seen)}</>}
              </p>
            )}
          </div>
          <button onClick={onClose} data-testid="journey-close" aria-label="Close"
            className="rounded-md p-1 text-neutral-500 hover:bg-neutral-800 hover:text-neutral-200">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 flex-1 overflow-y-auto pr-1">
          {error ? (
            <p className="text-sm text-red-400">{error}</p>
          ) : !data ? (
            <p className="inline-flex items-center gap-2 text-sm text-neutral-400">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading journey…
            </p>
          ) : (
            <>
              {data.current_stage && (
                <div className="mb-4 flex items-center gap-2 rounded-xl border border-[#F59E0B]/25 bg-[#F59E0B]/[0.07] px-3 py-2"
                  data-testid="journey-current-stage">
                  <Flag className="h-3.5 w-3.5 shrink-0 text-[#F59E0B]" />
                  <p className="text-xs text-neutral-200">
                    {data.current_stage === "Journey complete"
                      ? <b>Journey complete 🎉</b>
                      : <>Currently at: <b>{data.current_stage}</b>
                        {data.stuck_days != null && data.stuck_days > 0 && (
                          <span className="text-neutral-400"> — {data.stuck_days} day{data.stuck_days !== 1 ? "s" : ""} in this stage</span>
                        )}</>}
                  </p>
                </div>
              )}

              {data.founder_stage && (
                <div className="mb-4 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.05] px-3 py-2.5"
                  data-testid="journey-founder-stage">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-emerald-300">
                      Stage {data.founder_stage.stage}/7 · {data.founder_stage.label}
                    </span>
                    <TierBadge tier={data.founder_stage.tier} testid="journey-tier-badge" />
                    {data.founder_stage.matchmaking_ready && (
                      <span className="rounded-full bg-[#F59E0B]/15 px-2 py-0.5 text-[10px] font-extrabold uppercase text-[#F59E0B]"
                        data-testid="journey-matchmaking-ready">
                        Matchmaking Ready
                      </span>
                    )}
                  </div>
                  {data.founder_stage.prep_last_edited_at && (
                    <p className="mt-1.5 text-[11px] text-neutral-400" data-testid="journey-prep-last-edited">
                      Prep app — docs last edited {fmt(data.founder_stage.prep_last_edited_at)}
                    </p>
                  )}
                  {(data.founder_stage.history || []).length > 0 && (
                    <ol className="mt-2 space-y-0.5" data-testid="journey-stage-history">
                      {data.founder_stage.history.slice(-7).map((h, i) => (
                        <li key={i} className="flex items-center gap-2 text-[11px] text-neutral-300">
                          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />
                          Stage {h.stage} · {h.label}
                          <span className="ml-auto text-[10px] text-neutral-500">{fmt(h.at)}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              )}

              {milestones.length > 0 && (
                <ol className="space-y-0.5" data-testid="journey-milestones">
                  {milestones.map((m) => (
                    <li key={m.key} className="flex items-center gap-3 rounded-lg px-2 py-1.5"
                      data-testid={`journey-milestone-${m.key}`}>
                      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                        m.done ? "bg-emerald-500/15 text-emerald-400" : "border border-neutral-700 text-neutral-700"}`}>
                        {m.done ? <Check className="h-3 w-3" /> : null}
                      </span>
                      <span className={`text-sm ${m.done ? "text-neutral-200" : "text-neutral-500"}`}>
                        {m.label}
                      </span>
                      {m.detail && (
                        <span className="rounded-full bg-neutral-800 px-1.5 py-0.5 text-[10px] font-semibold text-neutral-300">
                          {m.detail}
                        </span>
                      )}
                      {m.at && <span className="ml-auto shrink-0 text-[10px] text-neutral-500">{fmt(m.at)}</span>}
                    </li>
                  ))}
                </ol>
              )}

              <button type="button" onClick={() => setShowViews((v) => !v)}
                data-testid="journey-toggle-views"
                className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-sky-300 hover:text-sky-200">
                {showViews ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                Raw page views ({data.total_views})
              </button>

              {showViews && (
                data.views.length === 0 ? (
                  <p className="mt-2 text-sm text-neutral-500" data-testid="journey-empty">
                    No tracked page views yet for this user.
                  </p>
                ) : (
                  <ol className="relative ml-2 mt-2 border-l border-neutral-800" data-testid="journey-timeline">
                    {data.views.map((v, i) => (
                      <li key={i} className="relative py-1.5 pl-5">
                        <span className={`absolute -left-[5px] top-3 h-2.5 w-2.5 rounded-full ${
                          v.phase === "visitor" ? "bg-amber-400" : "bg-emerald-400"}`} />
                        <div className="flex items-center gap-2">
                          <code className="truncate text-xs text-neutral-200">{v.path}</code>
                          <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase ${
                            v.phase === "visitor"
                              ? "bg-amber-500/10 text-amber-300"
                              : "bg-emerald-500/10 text-emerald-300"}`}>
                            {v.phase === "visitor"
                              ? <><Footprints className="h-2.5 w-2.5" /> visitor</>
                              : <><UserCheck className="h-2.5 w-2.5" /> signed in</>}
                          </span>
                          <span className="ml-auto shrink-0 text-[10px] text-neutral-500">{fmt(v.ts)}</span>
                        </div>
                      </li>
                    ))}
                  </ol>
                )
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
